import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Field from '../Field/Field';
import './Group.css';

const Group = ({ 
  group, 
  onDelete, 
  onToggleCollapse,
  onEditField, 
  onDeleteField, 
  onToggleField,
  onToggleFieldRequired,
  onToggleFieldAdminOnly,
  onMoveFieldUp, 
  onMoveFieldDown,
  onMoveGroupUp,
  onMoveGroupDown,
  onToggleSidebar,
  showToast,
  canMoveUp = false,
  canMoveDown = false,
  groupCollapsedStates
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: group.id,
    data: {
      type: 'group',
      groupId: group.id,
      groupType: group.type
    }
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 100 : 1,
    boxShadow: isDragging ? 'var(--hvnly-card-shadow)' : 'none',
  };
  
  // Get collapsed state from props or default to false
  const isCollapsed = group.collapsed !== undefined ? group.collapsed : false;
  
  // Check if this is a property_docs group
  const isPropertyDocsGroup = group.type === 'property_docs';
  
  // Sidebar visibility state - stored in group metadata
  const [showInSidebar, setShowInSidebar] = useState(
    group.show_in_sidebar !== undefined ? group.show_in_sidebar : true
  );
  
  // Update showInSidebar when group changes
  useEffect(() => {
    if (isPropertyDocsGroup && group.fields && group.fields.length > 0) {
      // Read show_in_sidebar from the first field (all fields in group should have same value)
      const sidebarStatus = group.fields[0]?.show_in_sidebar;
      if (sidebarStatus !== undefined) {
        setShowInSidebar(sidebarStatus);
      }
    }
  }, [group.fields, isPropertyDocsGroup]);
  
  const handleDeleteGroup = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (onDelete) {
      onDelete();
    }
  };
  
  const handleMoveGroupUp = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onMoveGroupUp) {
      onMoveGroupUp();
    }
  };
  
  const handleMoveGroupDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onMoveGroupDown) {
      onMoveGroupDown();
    }
  };
  
  const handleToggleCollapse = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onToggleCollapse();
  };
  
const handleToggleSidebar = (e) => {
  e.stopPropagation();
  e.preventDefault();
  
  const newValue = !showInSidebar;
  setShowInSidebar(newValue);
  
  // Update group metadata
  if (onToggleSidebar) {
    onToggleSidebar(group.id, newValue);
  }
  
  // Toast is now handled by the parent component through onToggleSidebar
  // No need to call showToast here - it's called in usePropertyBuilder
};
  
  // Sort fields by group_position
  const sortedFields = [...(group.fields || [])].sort(
    (a, b) => (a.group_position || 0) - (b.group_position || 0)
  );
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`hvnly--builder--int_api__property_builder_group 
        ${isDragging ? 'dragging' : ''} 
        ${isCollapsed ? 'collapsed' : ''}
        ${isPropertyDocsGroup ? 'hvnly--builder--int_api__property_docs_group' : ''}
        ${showInSidebar ? 'sidebar-visible' : 'sidebar-hidden'}`}
      data-group-id={group.id}
      data-group-type={group.type}
      data-sidebar-status={showInSidebar ? 'visible' : 'hidden'}
    >
      <div className="hvnly--builder--int_api__property_builder_group_header">
        <div 
          className="hvnly--builder--int_api__drag_handle"
          {...attributes}
          {...listeners}
          style={{ cursor: 'grab' }}
          title={__('Drag to reorder group', 'havenlytics')}
        >
          <i className="fas fa-grip-vertical"></i>
        </div>
        
        <button
          className="hvnly--builder--int_api__group_collapse_btn"
          onClick={handleToggleCollapse}
          title={isCollapsed ? __('Expand Group', 'havenlytics') : __('Collapse Group', 'havenlytics')}
          type="button"
        >
          <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'}`}></i>
        </button>
        
        <h4>
          <i className={`fas fa-${group.icon || (isPropertyDocsGroup ? 'file-pdf' : 'folder')}`}></i>
          {group.name || group.group_name || __('Group', 'havenlytics')}
          <span className="hvnly--builder--int_api__group_badge">
            {sortedFields.length} {sortedFields.length === 1 ? __('field', 'havenlytics') : __('fields', 'havenlytics')}
          </span>
          {group.type && (
            <span className="hvnly--builder--int_api__group_type_badge">
              {group.type === 'property_docs' ? __('Documents', 'havenlytics') : group.type}
            </span>
          )}
        </h4>
        
        {/* Sidebar toggle button - only for property docs group */}
        {isPropertyDocsGroup && (
          <button
            className={`hvnly--builder--int_api__sidebar_toggle_btn ${showInSidebar ? 'active' : ''}`}
            onClick={handleToggleSidebar}
            title={showInSidebar ? __('Hide from sidebar widget', 'havenlytics') : __('Show in sidebar widget', 'havenlytics')}
            type="button"
          >
            <i className={`fas fa-${showInSidebar ? 'eye' : 'eye-slash'}`}></i>
            <span className="toggle-label">
              {showInSidebar ? __('Sidebar', 'havenlytics') : __('Hidden', 'havenlytics')}
            </span>
          </button>
        )}
        
        <div className="hvnly--builder--int_api__property_builder_group_actions">
          {canMoveUp && (
            <button 
              className="hvnly--builder--int_api__property_builder_group_action_btn move-up-btn"
              onClick={handleMoveGroupUp}
              title={__("Move Group Up", "havenlytics")}
              type="button"
            >
              <i className="fas fa-arrow-up"></i>
            </button>
          )}
          
          {canMoveDown && (
            <button 
              className="hvnly--builder--int_api__property_builder_group_action_btn move-down-btn"
              onClick={handleMoveGroupDown}
              title={__("Move Group Down", "havenlytics")}
              type="button"
            >
              <i className="fas fa-arrow-down"></i>
            </button>
          )}
          
          <button 
            className="hvnly--builder--int_api__property_builder_group_action_btn delete-btn" 
            onClick={handleDeleteGroup}
            title={__("Delete Group", "havenlytics")}
            type="button"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
      
      {!isCollapsed && (
        <div className="hvnly--builder--int_api__property_builder_group_content">
          {sortedFields.map((field, index) => (
            <Field
              key={`${field.id}-${field.group_position || index}`}
              id={field.id}
              field={field}
              index={field.group_position || index}
              fieldsLength={sortedFields.length}
              onEdit={() => {
                onEditField(field.id);
              }}
              onDelete={() => onDeleteField(field.id)}
              onToggle={() => {
                onToggleField(field.id);
              }}
              onToggleRequired={() => {
                onToggleFieldRequired(field.id);
              }}
              onToggleAdminOnly={() => {
                onToggleFieldAdminOnly(field.id);
              }}
              onMoveUp={() => {
                onMoveFieldUp(field.id);
              }}
              onMoveDown={() => {
                onMoveFieldDown(field.id);
              }}
              showToast={showToast}
              isFieldLocked={field.locked}
              isGroupField={true}
              groupPosition={field.group_position}
              groupTotal={field.group_total}
            />
          ))}
        </div>
      )}
      
      {isCollapsed && (
        <div className="hvnly--builder--int_api__property_builder_group_collapsed_summary">
          <span>
            {sortedFields.length} {__('fields', 'havenlytics')}
            {isPropertyDocsGroup && (
              <span className="sidebar-status" data-status={showInSidebar ? 'visible' : 'hidden'}>
                <i className={`fas fa-${showInSidebar ? 'eye' : 'eye-slash'}`}></i>
                {showInSidebar ? __('Visible in sidebar', 'havenlytics') : __('Hidden from sidebar', 'havenlytics')}
              </span>
            )}
          </span>
          <button 
            className="hvnly--builder--int_api__group_expand_btn"
            onClick={handleToggleCollapse}
            type="button"
          >
            <i className="fas fa-chevron-right"></i> {__('Expand', 'havenlytics')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Group;